import React, { PropsWithChildren } from 'react'
import { concatStyles } from 'utils'

export type ButtonAlignment = 'left' | 'right' | 'center' | 'stretch'
export type ButtonType = 'button' | 'submit' | 'reset' | undefined
export type ButtonAppearance =
  | 'destructive'
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'actionable'
  | 'custom'
  | 'link'

export interface ButtonProps {
  appearance?: ButtonAppearance
  align?: ButtonAlignment
  className?: string
  style?: React.CSSProperties
  type?: ButtonType
  disabled?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  form?: string
}

const BUTTON_STYLES =
  'rounded-none font-heading font-bold text-secondary text-sm text-center px-6 py-3 w-auto border-[3px] border-actionable bg-white hover:bg-actionable hover:border-actionable hover:text-secondary transition-colors'
const LINK_STYLES = 'flex font-body text-primary underline'
const DISABLED_STYLES = 'opacity-50 cursor-not-allowed'

export const backgroundStyles = (appearance?: ButtonAppearance) => {
  switch (appearance) {
    case 'primary':
      return 'border-primary bg-white text-primary hover:bg-primary hover:text-white'
    case 'secondary':
      return 'border-secondary bg-white text-secondary hover:bg-secondary hover:text-white'
    case 'destructive':
      return 'border-error bg-white text-error hover:bg-error hover:text-white'
    case 'accent':
      return 'border-accent bg-white text-secondary hover:bg-accent'
    case 'custom':
    case 'link':
      return ''
    default:
      return 'border-actionable bg-white text-secondary hover:bg-actionable hover:border-actionable'
  }
}

const alignmentStyles = (alignment?: ButtonAlignment) => {
  switch (alignment) {
    case 'center':
      return 'w-auto mx-auto'
    case 'right':
      return 'w-auto ml-auto'
    case 'left':
      return 'w-auto mr-auto'
    case 'stretch':
      return 'w-full'
    default:
      return 'w-auto'
  }
}

export const Button: React.FC<PropsWithChildren<ButtonProps>> = ({
  align,
  appearance,
  children,
  className,
  disabled,
  style,
  onClick,
  form,
}) => {
  return (
    <button
      className={concatStyles([
        appearance === 'link' ? LINK_STYLES : BUTTON_STYLES,
        backgroundStyles(appearance),
        alignmentStyles(align),
        disabled ? DISABLED_STYLES : '',
        className,
      ])}
      disabled={disabled}
      style={style}
      onClick={onClick}
      form={form}
    >
      {children}
    </button>
  )
}

Button.displayName = 'Form.Button'
